import { type SomeJSONSchema } from 'ajv/dist/types/json-schema';
import { asRequired } from '../../utils/lang';
import {
  setValidationMessages,
  setValidationProperties,
} from '../../utils/validation';
import { PropertyTypes } from './property-types';
import {
  type DateTimeConstraints,
  Format,
  type ValidationStringType,
  type StringType,
} from '@wix/ambassador-forms-v4-form/types';
import { ValidationMode } from '@wix/ambassador-forms-v1-validation-result/types';
import { type FormatName } from 'ajv-formats/dist/formats';
import { type CustomFormat } from '../ajv/ajv-custom-formats';
import { stringFormatOptionsMapper } from './string-format-options-mapper';
import { STRING_VALIDATION_PROPERTIES } from '../schema-validation-properties';

const ALLOWED_MAX_LENGTH = 20000;

export function mapToStringSchemaProperty(
  stringField: StringType | ValidationStringType,
  validationMode: ValidationMode,
  libraryBasedPhoneFieldValidationEnabled: boolean = false,
): SomeJSONSchema & { type: 'string' } {
  const validationMessages = isStringType(stringField)
    ? setValidationMessages(STRING_VALIDATION_PROPERTIES, stringField)
    : {};
  return {
    type: PropertyTypes.STRING,
    maxLength: ALLOWED_MAX_LENGTH,
    ...validationMessages,
    ...resolveAjvFormat(stringField),
    ...resolveRestrictedValidationProperties(
      stringField,
      validationMode,
      libraryBasedPhoneFieldValidationEnabled,
    ),
  };
}

function isStringType(
  field: StringType | ValidationStringType,
): field is StringType {
  return 'validationMessages' in field;
}

function resolveRestrictedValidationProperties(
  stringField: StringType | ValidationStringType,
  validationMode: ValidationMode,
  libraryBasedPhoneFieldValidationEnabled: boolean,
) {
  if (validationMode !== ValidationMode.UNRESTRICTED_UPDATE) {
    return {
      ...setValidationProperties<number>(
        STRING_VALIDATION_PROPERTIES.filter(
          (property) => property !== 'format',
        ),
        stringField,
        {
          maxLength: (value) =>
            Math.min(value ?? ALLOWED_MAX_LENGTH, ALLOWED_MAX_LENGTH),
        },
      ),
      ...stringFormatOptionsMapper(
        stringField.format as Format,
        getDateTimeConstrains(stringField),
        undefined,
        stringField.phoneOptions,
        libraryBasedPhoneFieldValidationEnabled,
      ),
    };
  }
  return {};
}

function resolveAjvFormat(stringField: StringType | ValidationStringType) {
  const stringFormat =
    stringField.format &&
    formMap[asRequired(stringField.format, stringField, 'format')];
  if (!stringFormat) {
    return {};
  } else if (stringFormat.length === 1) {
    return { format: stringFormat[0] };
  } else {
    return {
      oneOf: stringFormat.map((format) => ({ format })),
    };
  }
}

const formMap: { [key: string]: (FormatName | CustomFormat)[] } = {
  [Format.DATE]: ['date'],
  [Format.TIME]: ['time'],
  [Format.DATE_TIME]: ['date-time'],
  [Format.EMAIL]: ['email'],
  [Format.URL]: ['url'],
  [Format.UUID]: ['uuid'],
  [Format.PHONE]: ['phone'],
  [Format.URI]: ['uri'],
  [Format.HOSTNAME]: ['hostname'],
  [Format.COLOR_HEX]: ['color-hex'],
  [Format.CURRENCY]: ['currency'],
  [Format.LANGUAGE]: ['language'],
  [Format.DATE_OPTIONAL_TIME]: ['date-time', 'date'],
  TIME_ZONE: ['time-zone'],
};

function getDateTimeConstrains(
  field: StringType | ValidationStringType,
): DateTimeConstraints | undefined {
  if ('dateOptions' in field) {
    return field.dateOptions;
  }
  if ('dateTimeOptions' in field) {
    return field.dateTimeOptions;
  }
  if ('timeOptions' in field) {
    return field.timeOptions;
  }
  if ('dateOptionalTimeOptions' in field) {
    return field.dateOptionalTimeOptions;
  }
  return undefined;
}
